import type { GraphQuery, NodeDetails, QueryFilter } from '@/types';

/**
 * Auto-normalizes forward `IN` filter values so the backend query reader binds
 * a Cypher LIST as the right-hand side of the `IN` predicate.
 *
 * ## Why this exists (ENG-3855)
 *
 * The backend query reader compiles a forward `IN` filter (`operator === 'IN'`,
 * no `reverseOperands`) by iterating each top-level element of `filter.value`
 * and binding it as a SCALAR Cypher right-hand side:
 *
 *     field IN $p1 OR field IN $p2 ...
 *
 * Cypher requires the RHS of `IN` to be a LIST. A flat array
 * (`['a','b','c']`) or a bare scalar therefore compiles to `field IN 'a'`,
 * which evaluates to `null` and MATCHES NOTHING.
 *
 * The correct shape nests the values one level so a single predicate is emitted
 * with the parameter bound as a list:
 *
 *     value: [['a','b','c']]  ->  field IN $p1   ($p1 = ['a','b','c'])
 *
 * This module normalizes forward `IN` values at the serialization chokepoint
 * (`flatGraphConversion` re-nesting for save/execute) so a flat array or scalar
 * is wrapped into the nested form instead of silently matching nothing.
 *
 * ## Symmetric pair: normalize on write / denormalize on read (ENG-3855 follow-up)
 *
 * `normalize*` is the WRITE half — `flatGraphConversion.unflatten` re-nests
 * forward `IN` values for save/execute so the wire shape is `[['a','b','c']]`.
 *
 * `denormalize*` is the READ half — the exact inverse. The editor's read
 * boundaries (the `LeafFilterCard` chip builder, the ~15 passthrough
 * `SimpleFilter` definitions routed through `useMultiValueInput`, and the
 * `NodeFilterGroup` no-match fallback) all assume a FLAT array and garble a
 * nested `[[...]]` into a single comma-joined chip on reload (and corrupt it on
 * edit → save). Rather than teach every read boundary about the nested shape,
 * `flatGraphConversion.flatten` calls `denormalizeFilterValues` to UN-nest at
 * the single load seam. The result: the in-memory `FlatGraph` stays in the flat
 * UI shape that every read path already renders correctly, while the write
 * chokepoint (`normalizeFilterValues` in `unflatten`) re-nests for the wire.
 * Flatten un-nests / pack+unflatten re-nests — a lossless round-trip.
 *
 * ## Exemptions (left untouched)
 *
 * - `reverseOperands: true` `IN` — emits `$p IN field` (membership in a
 *   list-valued property); a flat array or scalar is the correct shape there.
 * - `ANY_IN` — its only producer (rosterQuery.ts) already sends the nested
 *   `[[...]]` shape; this normalizer leaves it alone.
 * - Already-nested forward `IN` values (`[[...]]`) — already correct.
 * - Empty / nullish values (`[]`, `''`, `null`, `undefined`) — left for the
 *   validator to reject; wrapping them would mask the error.
 * - All other operators (`=`, `OR`, `AND`, etc.).
 */

/** A JSON primitive accepted as a scalar filter value. */
type Primitive = string | number | boolean;

/**
 * True for a non-empty scalar primitive. Empty string is excluded so we never
 * wrap a value the validator should reject.
 */
function isPrimitive(value: unknown): value is Primitive {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

/**
 * True for a non-empty array whose every element is a scalar primitive
 * (e.g. `['a','b','c']`). Distinguishes a value array from a nested-filter array.
 */
function isFlatPrimitiveArray(value: unknown): value is Primitive[] {
  return Array.isArray(value) && value.length > 0 && value.every(isPrimitive);
}

/**
 * True for an array of nested `QueryFilter` objects (the `value` shape of an
 * `OR`/`AND` group). Mirrors the detection used in `validateQuery`'s
 * `isFilterArray` so a `QueryFilter[]` is never mistaken for a primitive array.
 */
function isNestedFilterArray(value: unknown): value is QueryFilter[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === 'object' &&
    value[0] !== null &&
    'operator' in value[0]
  );
}

/**
 * Normalizes a single filter (pure — returns a new object, never mutates input).
 *
 * - `OR` / `AND` with a nested-filter array: recurse into the children.
 * - forward `IN` (`!reverseOperands`): wrap a scalar or flat primitive array.
 * - everything else: returned as-is.
 */
function normalizeFilter(filter: QueryFilter): QueryFilter {
  if (filter.operator === 'OR' || filter.operator === 'AND') {
    if (isNestedFilterArray(filter.value)) {
      return { ...filter, value: filter.value.map(normalizeFilter) };
    }
    return filter;
  }

  if (filter.operator === 'IN' && !filter.reverseOperands) {
    if (isPrimitive(filter.value) && filter.value !== '') {
      return { ...filter, value: [[filter.value]] };
    }
    if (isFlatPrimitiveArray(filter.value)) {
      return { ...filter, value: [filter.value] };
    }
  }

  return filter;
}

/**
 * Normalizes forward `IN` filter values across a filter array, recursing into
 * `OR`/`AND` groups. Pure: returns new arrays/objects and does not mutate the
 * input. `undefined` in → `undefined` out.
 */
export function normalizeFilterValues(
  filters?: QueryFilter[]
): QueryFilter[] | undefined {
  if (filters === undefined) {
    return undefined;
  }
  return filters.map(normalizeFilter);
}

/**
 * Exact inverse of `normalizeFilter` (pure — returns a new object, never mutates
 * input). Un-nests a forward `IN` value one level so the editor's read
 * boundaries receive the flat UI shape.
 *
 * - `OR` / `AND` with a nested-filter array: recurse into the children.
 * - forward `IN` (`!reverseOperands`): if the value is a single-element array
 *   whose sole element is a flat primitive array (the nested wire shape
 *   `[['a','b','c']]` or `[['a']]`), unwrap one level to `['a','b','c']` / `['a']`.
 *   Any other shape (already-flat `['a','b']`, scalar, empty) is left as-is.
 * - everything else: returned as-is.
 *
 * Idempotent: after the first unwrap `value[0]` is a primitive, not an array, so
 * `isFlatPrimitiveArray(filter.value[0])` is false and a second application is a
 * no-op.
 */
function denormalizeFilter(filter: QueryFilter): QueryFilter {
  if (filter.operator === 'OR' || filter.operator === 'AND') {
    if (isNestedFilterArray(filter.value)) {
      return { ...filter, value: filter.value.map(denormalizeFilter) };
    }
    return filter;
  }

  if (filter.operator === 'IN' && !filter.reverseOperands) {
    if (
      Array.isArray(filter.value) &&
      filter.value.length === 1 &&
      isFlatPrimitiveArray(filter.value[0])
    ) {
      return { ...filter, value: filter.value[0] };
    }
  }

  return filter;
}

/**
 * Inverse of `normalizeFilterValues`. Un-nests forward `IN` filter values across
 * a filter array, recursing into `OR`/`AND` groups. Pure: returns new
 * arrays/objects and does not mutate the input. `undefined` in → `undefined` out.
 */
export function denormalizeFilterValues(
  filters?: QueryFilter[]
): QueryFilter[] | undefined {
  if (filters === undefined) {
    return undefined;
  }
  return filters.map(denormalizeFilter);
}

/**
 * Normalizes every filter array reachable from a `NodeDetails` (its own
 * `filters`, plus each relationship's `filters` and the relationship's
 * `source`/`target` sub-nodes), recursing the full node/relationship tree.
 *
 * Pure: returns a new `NodeDetails` and never mutates the input. Spreads the
 * node so any field this normalizer does not touch is preserved verbatim.
 */
function normalizeNodeDetailsFilters(node: NodeDetails): NodeDetails {
  return {
    ...node,
    filters: normalizeFilterValues(node.filters),
    relationships: node.relationships?.map(relationship => ({
      ...relationship,
      filters: normalizeFilterValues(relationship.filters),
      ...(relationship.source && {
        source: normalizeNodeDetailsFilters(relationship.source),
      }),
      ...(relationship.target && {
        target: normalizeNodeDetailsFilters(relationship.target),
      }),
    })),
  };
}

/**
 * Normalizes forward `IN` filter values across an entire `GraphQuery` (the
 * nested API shape from `@/types`), rewriting BOTH the top-level
 * `GraphQuery.filters` (the "global" filters the backend applies in
 * `buildGlobalFilters`) AND every filter array inside the `node` tree
 * (recursing through `node` and every relationship's `source`/`target`).
 * Returns a NEW `GraphQuery`, preserving ALL other top-level fields (`limit`,
 * `orderBy`, `descending`, `shortest`, etc.).
 *
 * ## Why this exists (ENG-3855)
 *
 * `normalizeFilterValues` is only reached through the FlatGraph chokepoint
 * (`unflatten` re-nesting for save/execute). The raw-JSON writer path never
 * builds a `FlatGraph` for the value it ships — it returns the freshly parsed
 * `GraphQuery` straight to execute/save — so it bypasses that chokepoint
 * entirely. Without normalization here, a flat forward `IN`
 * (`operator: 'IN'`, no `reverseOperands`, value `['a','b','c']`) typed in the
 * JSON writer reaches the backend as the broken flat array and matches nothing.
 *
 * The top-level `GraphQuery.filters` are equally affected: the backend compiles
 * them in `buildGlobalFilters` (backend/pkg/query/read.go) through the SAME
 * `buildRegularFilter` compiler as node filters, so a flat forward `IN` in the
 * global filters compiles to the broken scalar RHS and matches nothing too.
 * `flatten`/`unflatten` do not carry the top-level `filters`, so they are NOT
 * normalized by the FlatGraph chokepoint — this function is the only place they
 * get normalized before execute/save.
 *
 * This walks the `GraphQuery` directly so the writer can normalize the exact
 * object it executes and saves. `normalizeFilterValues(undefined)` returns
 * `undefined`, so omitting top-level `filters` is preserved verbatim.
 * Idempotent — it inherits `normalizeFilterValues`' idempotency.
 */
export function normalizeQueryFilters(query: GraphQuery): GraphQuery {
  return {
    ...query,
    filters: normalizeFilterValues(query.filters),
    node: normalizeNodeDetailsFilters(query.node),
  };
}
